import React, { useState, useMemo } from "react";
import { XCrossIcon } from "../../assets";
import { AllianceChargeLine, trimDecimals } from "../../utils/utils";

interface RowDataType {
  serial: number;
  serviceName: string;
  carrierName: string;
  description: string;
  serviceLevel: string;
  quotePrice: any; // FM total (dollars)
  agentName: string;
  price: any;
  markup: number;
  priceWithMarkUp: string;
  fuelSurcharge: any; // legacy fallback
  additionalCharges?: any[];
  additionalOtherCharges?: { calculatedCharges: any[] }; // legacy fallback
  basePrice?: string | number; // legacy fallback
  priceWithFinalMile?: string | number; // LTL + FM total
  mode?: string; // "Service:" line
  internalLTL?: boolean;
  quoteFrom?: string;
  isProvidence?: boolean;
  accessorialCharges?: any;
  ltlFuel?: any;
  ltlBaseRate?: any;
  ltlAccessorialCharges: any;
  selectedFinalMileQuote?: any;
  orderFor?: string;
  lineItems?: any[];
  allianceCharges?: AllianceChargeLine[];
}

interface PriceDetailsModalType {
  rowData?: Partial<RowDataType>;
  setConfirmModal: (value: boolean) => void;
  ltlFMData?: any;
}

const LTLPriceDetailsModal: React.FC<PriceDetailsModalType> = (props) => {
  const { rowData, setConfirmModal, ltlFMData } = props;
  const handleClose = () => setConfirmModal(false);
  const [details] = useState<any>(rowData);

  // ---------- STRICT: use rateDetails if present ----------
  const {
    fmBaseDisplay,
    fmFuelDisplay,
    fmOtherCharges,
    fmTotalDisplay,
    fmServiceName,
  } = useMemo(() => {
    const rd = rowData?.internalLTL
      ? ltlFMData?.services?.[0]?.rateDetails
      : ltlFMData?.finalMileData?.service?.rateDetails;
    const hasRD = rd && typeof rd === "object" && Object.keys(rd).length > 0;

    const svcName =
      rowData?.mode ||
      rowData?.serviceName ||
      ltlFMData?.finalMileData?.service?.name ||
      "";

    const base = hasRD
      ? Number(rd.basePrice ?? rd.serviceCharge ?? 0)
      : Number(rowData?.basePrice ?? 0);

    // ✅ Changed precedence: prefer fuelSurcharge, then fall back to customerFuelSurcharge
    const fuel = hasRD
      ? Number(rd.customerFuelSurcharge ?? rd.fuelSurcharge ?? 0)
      : Number(
        rowData?.internalLTL
          ? rowData?.fuelSurcharge
          : rowData?.selectedFinalMileQuote?.service?.rateDetails
            ?.customerFuelSurcharge ?? 0
      );

    // const otherCalc = hasRD
    //   ? rd.additionalOtherCharges?.calculatedCharges ?? []
    //   : rowData?.additionalOtherCharges?.calculatedCharges ?? [];
    const otherCalc = hasRD
      ? rd?.additionalOtherCharges?.calculatedCharges
      : rowData?.internalLTL
        ? rd?.additionalOtherCharges?.calculatedCharges ||
        rowData?.additionalCharges
        : rowData?.additionalOtherCharges?.calculatedCharges ?? [];

    const totalFm = hasRD
      ? Number(
        rd.totalCustomerCharges ?? rd.serviceChargeWithMarkup ?? rd.total ?? 0
      )
      : Number(
        rowData?.internalLTL
          ? rowData?.price
          : rowData?.quotePrice ?? rowData?.price ?? 0
      );

    return {
      fmBaseDisplay: Number.isFinite(base) ? base : 0,
      fmFuelDisplay: Number.isFinite(fuel) ? fuel : 0,
      fmOtherCharges: Array.isArray(otherCalc) ? otherCalc : [],
      fmTotalDisplay: Number.isFinite(totalFm) ? totalFm : 0,
      fmServiceName: svcName,
    };
  }, [rowData, ltlFMData]);

  const {
    selectedFmBaseDisplay,
    selectedFmFuelDisplay,
    selectedFmOtherCharges,
    selectedFmTotalDisplay,
    selectedFmServiceName,
  } = useMemo(() => {
    const rd = rowData?.selectedFinalMileQuote?.service?.rateDetails;
    const hasRD = rd && typeof rd === "object" && Object.keys(rd).length > 0;
    const svcName =
      rowData?.mode ||
      rowData?.serviceName ||
      rowData?.selectedFinalMileQuote?.service?.name ||
      "";

    const base = hasRD
      ? Number(rd.basePrice ?? rd.serviceCharge ?? 0)
      : Number(rowData?.basePrice ?? 0);

    // ✅ Changed precedence: prefer fuelSurcharge, then fall back to customerFuelSurcharge
    const fuel = hasRD
      ? Number(rd.customerFuelSurcharge ?? rd.fuelSurcharge ?? 0)
      : Number(rowData?.fuelSurcharge ?? 0);

    const otherCalc = hasRD
      ? rd.additionalOtherCharges?.calculatedCharges ?? []
      : rowData?.additionalOtherCharges?.calculatedCharges ?? [];

    const totalFm = hasRD
      ? Number(
        rd.totalCustomerCharges ?? rd.serviceChargeWithMarkup ?? rd.total ?? 0
      )
      : Number(rowData?.quotePrice ?? rowData?.price ?? 0);

    return {
      selectedFmBaseDisplay: Number.isFinite(base) ? base : 0,
      selectedFmFuelDisplay: Number.isFinite(fuel) ? fuel : 0,
      selectedFmOtherCharges: Array.isArray(otherCalc) ? otherCalc : [],
      selectedFmTotalDisplay: Number.isFinite(totalFm) ? totalFm : 0,
      selectedFmServiceName: svcName,
    };
  }, [rowData, ltlFMData]);

  // Alliance returns a flat freight charge + accessorial bag, so `additionalCharges`
  // is empty for it and the table below would render nothing. The mapper flattens
  // that shape into ready-to-draw rows.
  const allianceChargeLines = rowData?.allianceCharges ?? [];

  return (
    <div className="modal modal--hazmatDetail modal--open">
      <div className="modal__dialog">
        <div className="modal__content modal__content--lg">
          <div className="modal__header">
            <h4 className="modal__header--title">Price Details</h4>
            <button
              className="btn btn__transparent btnClose"
              onClick={handleClose}
            >
              <XCrossIcon />
            </button>
          </div>

          <div className="modal__body modal-body-left">
            <div className="orderPriceDetial">
              <h3 className="title title--h5">
                Carrier Name: {rowData?.carrierName ?? ""}
              </h3>
              <h4 className="title title--h5">
                Service: {fmServiceName || details["mode"]}
              </h4>

              {!rowData || !rowData.quotePrice ? (
                // -------- LTL only --------
                <div
                  className="orderPriceDetial__history"
                  style={{ marginLeft: "2px" }}
                >
                  <div className="orderPriceDetial__history__header">
                    <div className="item">Charge</div>
                    <div className="item">Price</div>
                  </div>
                  <div className="orderPriceDetial__history__body">
                    {allianceChargeLines.length > 0
                      ? allianceChargeLines.map((charge, idx) => (
                          <div className="item" key={idx}>
                            <div className="text">{charge.name}</div>
                            <div className="value">${trimDecimals(charge.amount)}</div>
                          </div>
                        ))
                      : (rowData?.quoteFrom === "armstrong" || rowData?.orderFor === "armstrong") && Array.isArray(rowData?.lineItems) && rowData.lineItems.length > 0
                      ? rowData.lineItems.map((item: any, idx: number) => (
                          <div className="item" key={idx}>
                            <div className="text">{item.type}</div>
                            <div className="value">${trimDecimals(item.customerRate)}</div>
                          </div>
                        ))
                      : rowData?.additionalCharges &&
                        rowData.additionalCharges.map((additionalCharge, idx) => (
                          <div className="item" key={idx}>
                            <div className="text">
                              {additionalCharge.customerDescription ||
                                additionalCharge?.name}
                            </div>
                            <div className="value">
                              $
                              {trimDecimals(
                                additionalCharge?.customerPrice?.value ||
                                additionalCharge?.amount
                              )}
                            </div>
                          </div>
                        ))}
                  </div>
                  <div className="orderPriceDetial__history__footer">
                    <div className="item">Total Price</div>
                    <div className="item">
                      $
                      {trimDecimals(
                        (rowData?.priceWithMarkUp as any) || rowData?.price
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                // -------- LTL + Final Mile --------
                <>
                  <div className="formRow formRow__2">
                    {/* LTL Column */}
                    {!rowData?.internalLTL && (
                      <div className="formCol">
                        <div className="orderPriceDetial__history">
                          <div className="orderPriceDetial__history__header">
                            <div className="item">Charge(LTL)</div>
                            <div className="item">Price</div>
                          </div>

                          <div className="orderPriceDetial__history__body">
                            {rowData?.quoteFrom === "providence" || rowData?.isProvidence ||
                              ltlFMData?.quoteFrom === "providence" ? (
                              <>
                                <div className="item">
                                  <div className="text">Base Price</div>
                                  <div className="value">
                                    $
                                    {trimDecimals(
                                      rowData?.ltlBaseRate ??
                                      rowData?.basePrice ??
                                      0
                                    )}
                                  </div>
                                </div>

                                <div className="item">
                                  <div className="text">Fuel Surcharge</div>
                                  <div className="value">
                                    $
                                    {trimDecimals(
                                      rowData?.ltlFuel ??
                                      rowData?.fuelSurcharge ?? // Traditional Quote
                                      0
                                    )}
                                  </div>
                                </div>

                                {((rowData?.ltlAccessorialCharges != null &&
                                  rowData.ltlAccessorialCharges > 0) ||
                                  (rowData?.accessorialCharges != null &&
                                    rowData.accessorialCharges > 0)) && (
                                    <div className="item">
                                      <div className="text">
                                        Accessorial Charges
                                      </div>
                                      <div className="value">
                                        $
                                        {trimDecimals(
                                          rowData?.ltlAccessorialCharges ??
                                          rowData?.accessorialCharges ??
                                          0
                                        )}
                                      </div>
                                    </div>
                                  )}
                              </>
                            ) : null}
                            {(rowData?.quoteFrom === "armstrong" || rowData?.orderFor === "armstrong") && Array.isArray(rowData?.lineItems) && rowData.lineItems.length > 0
                              ? rowData.lineItems.map((item: any, idx: number) => (
                                  <div className="item" key={idx}>
                                    <div className="text">{item.type}</div>
                                    <div className="value">${trimDecimals(item.customerRate)}</div>
                                  </div>
                                ))
                              : rowData?.additionalCharges &&
                                rowData.additionalCharges.map(
                                  (additionalCharge, idx) => (
                                    <div className="item" key={idx}>
                                      <div className="text">
                                        {additionalCharge.customerDescription ||
                                          additionalCharge?.name}
                                      </div>
                                      <div className="value">
                                        $
                                        {trimDecimals(
                                          additionalCharge?.customerPrice
                                            ?.value || additionalCharge?.amount
                                        )}
                                      </div>
                                    </div>
                                  )
                                )}
                          </div>
                          <div className="orderPriceDetial__history__footer">
                            <div className="item">Price</div>
                            <div className="item">
                              $
                              {trimDecimals(
                                (rowData?.priceWithMarkUp as any) ||
                                rowData?.price
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Final Mile Column */}
                    <div className="formCol">
                      <div className="orderPriceDetial__history">
                        <div className="orderPriceDetial__history__header">
                          <div className="item">
                            Charge(
                            {!rowData?.internalLTL ? "Final Mile" : "LTL"})
                          </div>
                          <div className="item">Price</div>
                        </div>

                        <div className="orderPriceDetial__history__body">
                          <div className="item">
                            <div className="text">Base Price</div>
                            <div className="value">
                              ${trimDecimals(fmBaseDisplay)}
                            </div>
                          </div>

                          <div className="item">
                            <div className="text">Fuel Surcharge</div>
                            <div className="value">
                              ${trimDecimals(fmFuelDisplay)}
                            </div>
                          </div>

                          {fmOtherCharges.map(
                            (additionalCharge: any, idx: number) => (
                              <div className="item" key={idx}>
                                <div className="text">
                                  {additionalCharge.name}
                                </div>
                                <div className="value">
                                  ${trimDecimals(additionalCharge?.value)}
                                </div>
                              </div>
                            )
                          )}
                        </div>

                        <div className="orderPriceDetial__history__footer">
                          <div className="item">Price</div>
                          <div className="item">
                            $
                            {trimDecimals(
                              fmTotalDisplay ||
                              (rowData?.quotePrice as any) ||
                              rowData?.price
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Second Final Mile Column - only when rowData?.internalLTL when data is from internal LTL + final mile*/}
                    {rowData?.internalLTL && (
                      <div className="formCol">
                        <div className="orderPriceDetial__history">
                          <div className="orderPriceDetial__history__header">
                            <div className="item">Charge(Final Mile)</div>
                            <div className="item">Price</div>
                          </div>

                          <div className="orderPriceDetial__history__body">
                            <div className="item">
                              <div className="text">Base Price</div>
                              <div className="value">
                                ${trimDecimals(selectedFmBaseDisplay)}
                              </div>
                            </div>

                            <div className="item">
                              <div className="text">Fuel Surcharge</div>
                              <div className="value">
                                ${trimDecimals(selectedFmFuelDisplay)}
                              </div>
                            </div>

                            {selectedFmOtherCharges.map(
                              (additionalCharge: any, idx: number) => (
                                <div className="item" key={idx}>
                                  <div className="text">
                                    {additionalCharge.name}
                                  </div>
                                  <div className="value">
                                    ${trimDecimals(additionalCharge?.value)}
                                  </div>
                                </div>
                              )
                            )}
                          </div>

                          <div className="orderPriceDetial__history__footer">
                            <div className="item">Price</div>
                            <div className="item">
                              $
                              {trimDecimals(
                                selectedFmTotalDisplay ||
                                (rowData?.quotePrice as any) ||
                                rowData?.price
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <h3 className="title title--h5">
                    Total Price: $
                    {trimDecimals(
                      (rowData?.priceWithFinalMile as number) ??
                      (ltlFMData?.totalPrice as number) ??
                      0
                    )}
                  </h3>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="modal__backdrop"></div>
    </div>
  );
};

export default LTLPriceDetailsModal;
